(function () {
  "use strict";

  const VERSION = "0.3.1";

  // Copy override helper — QB.msg is provided by the drawer core; this
  // fallback keeps the slot standalone-safe.
  function msg(key, fallback) {
    return window.QB?.msg ? window.QB.msg(key, fallback) : fallback;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // audience: "all" | "guest" | "new" | "member" | a tier name (matched
  // against the customer's current tier, case-insensitive).
  // "new" = not logged in AND not recognized as an existing customer by
  // qb-known.js — the audience for first-order signup offers.
  function audienceMatch(audience, customer) {
    const aud = String(audience || "all").toLowerCase();
    if (aud === "all") return true;
    const loggedIn = Boolean(customer?.loggedIn);
    if (aud === "guest") return !loggedIn;
    if (aud === "new") return !loggedIn && !window.QB?.known?.isKnown;
    if (aud === "member") return loggedIn;
    return loggedIn && String(customer.tier || "").toLowerCase() === aud;
  }

  function isValidHttpUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.protocol === "https:" || parsed.protocol === "http:";
    } catch {
      return false;
    }
  }

  class QBPromosSlot {
    constructor(panel, config) {
      this.panel = panel;
      this.config = config;
      this.workerUrl = window.QB?.worker?.url || "";
      this.promos = [];
      this.subscribed = false;
      this.error = null;

      // Birthday nudge state (docs/BIRTHDAY_FEATURE.md — Hub capture surface)
      this.birthday = null; // { points } when the Worker has the feature on
      this.bdaySaved = false;
      this.bdaySaving = false;
      this.bdayError = null;

      this.bind();
      this.render();
      this.load();

      // Late recognition (worker ledger reply) can retarget "new"-audience
      // cards. Re-render only when it can't interrupt the visitor mid-signup.
      document.addEventListener("qb:known", () => {
        const input = this.panel.querySelector("[data-qb-promo-email]");
        const busy = input && (input.value.trim() !== "" || document.activeElement === input);
        if (!this.subscribed && !busy) this.render();
      });
    }

    visiblePromos() {
      const customer = window.QB?.customer;
      return this.promos.filter((p) => audienceMatch(p.audience, customer));
    }

    // Cached per-slot customer token. Member/tier promo cards (and their
    // codes) are revealed server-side only when this rides on /promos —
    // guests get a code-free response.
    async authToken(customer) {
      if (this._token) return this._token;
      if (!customer?.loggedIn || !customer.id || !customer.email) return null;
      // Shared session token (qb-auth.js) — one mint per session for every slot.
      if (window.QB && typeof window.QB.token === "function") {
        try {
          this._token = (await window.QB.token(customer)) || null;
        } catch {
          this._token = null;
        }
        return this._token;
      }
      try {
        const res = await fetch(`${this.workerUrl}/auth`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "token", customerId: customer.id, email: customer.email }),
        });
        const auth = await res.json();
        if (res.ok && auth.token) this._token = auth.token;
      } catch {
        /* fall back to the guest promo view */
      }
      return this._token || null;
    }

    async load() {
      if (!this.workerUrl) return;
      try {
        const token = await this.authToken(window.QB?.customer);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        // Wait for merchant copy alongside the promos fetch so the first
        // render already uses any overridden strings.
        const [res] = await Promise.all([
          fetch(`${this.workerUrl}/promos`, { headers }),
          window.QB?.messagesReady || Promise.resolve(),
        ]);
        const data = await res.json();
        if (!res.ok || !Array.isArray(data.promos)) return;
        this.promos = data.promos;
        this.birthday = data.birthday && typeof data.birthday === "object" ? data.birthday : null;
        this.render();
        this.offerTeaser();
        this.maybeAutoOpen();
      } catch {
        /* leave the empty state */
      }
    }

    // Hub-rendered birthday capture card: shown to logged-in customers with
    // at least one order and no stored birthday. Posts to the Worker's
    // /profile/birthday — deliberately NOT a Klaviyo form (single write
    // path). Dismiss lasts the session; "No thanks" is permanent.
    shouldShowBirthday() {
      const customer = window.QB?.customer;
      if (!this.birthday || !customer?.loggedIn) return false;
      if (this.bdaySaved) return true; // keep the success state visible
      if (customer.birthday) return false;
      if ((customer.ordersCount ?? 0) < 1) return false;
      try {
        if (localStorage.getItem("qbBdayDeclined")) return false;
      } catch {
        /* no storage — still offer */
      }
      try {
        if (sessionStorage.getItem("qbBdayDismissed")) return false;
      } catch {
        /* fine */
      }
      return true;
    }

    buildBirthdayCard() {
      const parts = [`<div class="qb-promos__card qb-promos__card--birthday" data-qb-bday>`];

      if (this.bdaySaved) {
        parts.push(
          `<p class="qb-promos__success">${escapeHtml(
            msg("birthday_success", "Sorted! Your birthday treat is locked in.")
          )}</p>`
        );
        parts.push(`</div>`);
        return parts.join("");
      }

      const points = this.birthday?.points;
      const defaultBody = points
        ? `Tell us your birthday and we'll drop ${points} bonus points into your account on the day.`
        : "Tell us your birthday and we'll send a treat on the day.";

      parts.push(`<button class="qb-promos__bday-dismiss" type="button" data-qb-bday-dismiss aria-label="Not now">&times;</button>`);
      parts.push(`<p class="qb-promos__title">${escapeHtml(msg("birthday_title", "Get a birthday treat"))}</p>`);
      parts.push(`<p class="qb-promos__body">${escapeHtml(msg("birthday_body", defaultBody))}</p>`);
      // Two labelled fields on purpose: a free-form "05-06" is ambiguous and
      // the server can't disambiguate it.
      parts.push(`
        <div class="qb-promos__bday-row">
          <label class="qb-promos__bday-field">Day
            <input data-qb-bday-dd type="number" inputmode="numeric" min="1" max="31" placeholder="DD" aria-label="Birthday day" value="${escapeHtml(this.bdayDd ?? "")}" />
          </label>
          <label class="qb-promos__bday-field">Month
            <input data-qb-bday-mm type="number" inputmode="numeric" min="1" max="12" placeholder="MM" aria-label="Birthday month" value="${escapeHtml(this.bdayMm ?? "")}" />
          </label>
          <button class="qb-promos__btn" type="button" data-qb-bday-save${this.bdaySaving ? " disabled" : ""}>
            ${this.bdaySaving ? "Saving…" : "Save"}
          </button>
        </div>
        <button class="qb-promos__bday-decline" type="button" data-qb-bday-decline>No thanks</button>
      `);
      if (this.bdayError) {
        parts.push(`<p class="qb-promos__error" role="alert">${escapeHtml(this.bdayError)}</p>`);
      }
      parts.push(`</div>`);
      return parts.join("");
    }

    // Hand the first matching teaser to the drawer core — it owns the chip's
    // session/dismissal rules.
    offerTeaser() {
      if (this.config.teaser === false) return;
      const promo = this.visiblePromos().find((p) => p.teaser);
      if (promo) {
        window.QB?.drawer?.showTeaser?.(promo.teaser, "promos");
      }
    }

    // Popup-style behavior, contained in the hub: opens the drawer on this
    // slot once per visitor. Off by default; enable per client via
    // slotsConfig.promos.autoOpen = { enabled: true, delaySeconds: 8 }.
    maybeAutoOpen() {
      const auto = this.config.autoOpen;
      if (!auto?.enabled) return;
      if (window.QB?.customer?.loggedIn) return;
      if (!this.visiblePromos().length) return;
      try {
        if (localStorage.getItem("qbAutoOpened")) return;
        localStorage.setItem("qbAutoOpened", "1");
      } catch {
        return; // no storage → would fire every page view; don't
      }
      const delay = Math.max(2, auto.delaySeconds ?? 8) * 1000;
      setTimeout(() => {
        if (!window.QB?.drawer?.isOpen) window.QB?.drawer?.open?.("promos");
      }, delay);
    }

    buildCard(promo) {
      const parts = [`<div class="qb-promos__card" data-qb-promo="${escapeHtml(promo.id)}">`];
      if (promo.image && isValidHttpUrl(promo.image)) {
        parts.push(
          `<img class="qb-promos__image" loading="lazy" src="${escapeHtml(promo.image)}" alt="${escapeHtml(promo.imageAlt || promo.title || "")}" />`
        );
      }
      parts.push(`<p class="qb-promos__title">${escapeHtml(promo.title)}</p>`);
      if (promo.body) parts.push(`<p class="qb-promos__body">${escapeHtml(promo.body)}</p>`);

      if (promo.type === "locked") {
        // Members-only teaser: no code or offer data reaches the browser —
        // the reveal happens server-side once /promos sees them as member.
        parts.push(`
          <a class="qb-promos__btn" href="${escapeHtml(window.QB?.loginUrl?.("promos") || "/account/login")}">Sign in to unlock</a>
          <p class="qb-promos__fine">New here? <a href="${escapeHtml(window.QB?.registerUrl?.("promos") || "/account/register")}">Creating an account</a> takes a minute — deals and rewards included.</p>
        `);
        parts.push(`</div>`);
        return parts.join("");
      }

      if (promo.type === "signup") {
        if (this.subscribed) {
          parts.push(
            `<p class="qb-promos__success">${escapeHtml(
              msg("signup_success", "You're in! Check your inbox for your code.")
            )}</p>`
          );
        } else {
          parts.push(`
            <div class="qb-promos__signup">
              <input class="qb-promos__email" data-qb-promo-email type="email" placeholder="Your email" aria-label="Email address" />
              <button class="qb-promos__btn" type="button" data-qb-promo-subscribe>Get my code</button>
            </div>
            <p class="qb-promos__fine">${escapeHtml(
              msg("signup_fine_print", "By signing up you agree to receive marketing emails. Unsubscribe anytime.")
            )}</p>
          `);
          if (this.error) {
            parts.push(`<p class="qb-promos__error" role="alert">${escapeHtml(this.error)}</p>`);
          }
        }
      } else {
        if (promo.code) {
          parts.push(`
            <div class="qb-promos__code-row">
              <code class="qb-promos__code">${escapeHtml(promo.code)}</code>
              <button class="qb-promos__copy" type="button" data-qb-promo-copy="${escapeHtml(promo.code)}">Copy</button>
            </div>
          `);
        }
        if (promo.ctaLabel && promo.ctaUrl && isValidHttpUrl(promo.ctaUrl)) {
          parts.push(
            `<a class="qb-promos__btn qb-promos__btn--link" href="${escapeHtml(promo.ctaUrl)}">${escapeHtml(promo.ctaLabel)}</a>`
          );
        }
      }

      parts.push(`</div>`);
      return parts.join("");
    }

    render() {
      const visible = this.visiblePromos();
      const cards = [];

      if (this.shouldShowBirthday()) cards.push(this.buildBirthdayCard());
      cards.push(...visible.map((p) => this.buildCard(p)));

      if (!cards.length) {
        this.panel.innerHTML = `
          <div class="qb-promos qb-promos--empty">
            <p class="qb-promos__message">${escapeHtml(
              msg("promos_empty", "No offers right now — check back soon!")
            )}</p>
          </div>
        `;
        return;
      }

      this.panel.innerHTML = `
        <div class="qb-promos">
          ${cards.join("")}
        </div>
      `;
    }

    bind() {
      this.panel.addEventListener("click", async (e) => {
        const copy = e.target.closest("[data-qb-promo-copy]");
        if (copy) {
          try {
            await navigator.clipboard.writeText(copy.dataset.qbPromoCopy);
            copy.textContent = "Copied!";
            setTimeout(() => (copy.textContent = "Copy"), 2000);
          } catch {
            copy.textContent = copy.dataset.qbPromoCopy; // clipboard blocked — show it
          }
          return;
        }
        if (e.target.closest("[data-qb-promo-subscribe]")) {
          await this.subscribe();
          return;
        }
        if (e.target.closest("[data-qb-bday-save]")) {
          await this.saveBirthday();
          return;
        }
        if (e.target.closest("[data-qb-bday-dismiss]")) {
          try {
            sessionStorage.setItem("qbBdayDismissed", "1");
          } catch {
            /* fine */
          }
          this.render();
          return;
        }
        if (e.target.closest("[data-qb-bday-decline]")) {
          try {
            localStorage.setItem("qbBdayDeclined", "1");
          } catch {
            /* fine */
          }
          this.render();
        }
      });

      this.panel.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && e.target.matches("[data-qb-promo-email]")) {
          e.preventDefault();
          this.subscribe();
        }
        if (e.key === "Enter" && e.target.matches("[data-qb-bday-dd], [data-qb-bday-mm]")) {
          e.preventDefault();
          this.saveBirthday();
        }
      });
    }

    // Both capture surfaces share ONE write path: UI -> POST /profile/birthday
    // -> metafield + Klaviyo mirror + D1 index. Auth is the same token mint
    // the rewards/chat slots use.
    async saveBirthday() {
      if (this.bdaySaving) return;
      const customer = window.QB?.customer;
      if (!this.workerUrl || !customer?.loggedIn) return;

      this.bdayDd = this.panel.querySelector("[data-qb-bday-dd]")?.value ?? "";
      this.bdayMm = this.panel.querySelector("[data-qb-bday-mm]")?.value ?? "";
      const dd = parseInt(this.bdayDd, 10);
      const mm = parseInt(this.bdayMm, 10);
      this.bdayError = null;

      const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (!mm || mm < 1 || mm > 12 || !dd || dd < 1 || dd > daysInMonth[(mm || 1) - 1]) {
        this.bdayError = "That date doesn't look right — check the day and month.";
        this.render();
        return;
      }
      const ddmm = `${String(dd).padStart(2, "0")}-${String(mm).padStart(2, "0")}`;

      this.bdaySaving = true;
      this.render();

      try {
        const token = await this.authToken(customer);
        if (!token) throw new Error("auth_failed");

        const res = await fetch(`${this.workerUrl}/profile/birthday`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ customerId: customer.id, birthday: ddmm, email: customer.email }),
        });
        const data = await res.json();
        if (!res.ok && data.error !== "already_set") throw new Error(data.error || "save_failed");

        this.bdaySaved = true;
        customer.birthday = ddmm; // hide the nudge for the rest of this view
      } catch (err) {
        this.bdayError =
          err.message === "rate_limited"
            ? "Too many tries — give it a minute."
            : "Couldn't save that just now — please try again.";
      }
      this.bdaySaving = false;
      this.render();
    }

    async subscribe() {
      const input = this.panel.querySelector("[data-qb-promo-email]");
      const email = input?.value.trim() || "";
      this.error = null;

      if (!EMAIL_RE.test(email)) {
        this.error = "That email doesn't look right — try again.";
        this.render();
        return;
      }
      if (!this.workerUrl) return;

      try {
        // token: lets the worker attach click-time fbp/fbc from the device
        // dossier to the server-side Lead event (meta-capi.js).
        let deviceToken;
        try {
          deviceToken = localStorage.getItem("qb_device_token") || undefined;
        } catch {
          /* no storage — Lead still sends with email + IP/UA */
        }
        const res = await fetch(`${this.workerUrl}/subscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            source: "qb_hub_signup",
            token: deviceToken,
            url: window.location.href,
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "subscribe_failed");
        this.subscribed = true;
        // They've identified themselves — stop showing signup surfaces on
        // this browser from now on.
        window.QB?.known?.mark?.({ email });
      } catch (err) {
        this.error =
          err.message === "rate_limited"
            ? "Too many tries — give it a minute."
            : "Couldn't sign you up just now — please try again.";
      }
      this.render();
    }
  }

  window.QB = window.QB || {};
  window.QB.slotModules = window.QB.slotModules || {};
  window.QB.slotModules.promos = {
    version: VERSION,
    init(panel) {
      const config = window.QB?.slots?.promos || {};
      return new QBPromosSlot(panel, config);
    },
  };
})();
